import type { Attendance } from "@book-club/shared";
import {
    ATTENDANCE_STATUS,
    ATTENDANCE_STATUS_LABELS,
    type AttendanceStatus,
} from "@/features/sessions/constants/attendanceStatus";

/**
 * Check if an attendance record is unconfirmed
 * Unconfirmed means:
 * - cancelledAt is null/undefined
 * - attendedAt is null/undefined
 * - resolvedAt is null/undefined (optional check)
 * @param attendance - Attendance record to check
 * @returns true if unconfirmed, false otherwise
 */
export function isUnconfirmedAttendance(attendance: Attendance): boolean {
    return attendance.cancelledAt == null && attendance.attendedAt == null && attendance.resolvedAt == null;
}

/**
 * Determine the status of an attendance record
 *
 * @param attendance - Attendance record to check
 * @returns AttendanceStatus
 */
export function getAttendanceStatus(attendance: Attendance): "cancelled" | "attended" | "absent" | "unconfirmed" {
    if (attendance.wasAbsent) {
        // Check absent (if wasAbsent is true, they're absent even if attendedAt is set)
        return ATTENDANCE_STATUS.ABSENT;
    }

    if (attendance.attendedAt != null) {
        // if attended
        return ATTENDANCE_STATUS.ATTENDED;
    }

    if (attendance.cancelledAt != null) {
        // if they cancelled
        return ATTENDANCE_STATUS.CANCELLED;
    }

    // Otherwise unconfirmed
    return ATTENDANCE_STATUS.UNCONFIRMED;
}

/**
 * Format attendance status for display
 *
 * @param status - Status to format
 * @returns Formatted status string
 */
export function formatAttendanceStatus(status: AttendanceStatus): string {
    return ATTENDANCE_STATUS_LABELS[status] ?? "Unknown";
}
